import type { FC, ReactNode } from 'react'
import type { Education as EducationRow } from '@/types'

import { useRef } from 'react'
import { cms } from '@/lib/cms'
import { pad } from '@/lib/format'
import { useReveal } from '@/hooks/use-reveal'
import { ArrowLink } from '@/components/generals/arrow-link'
import { Marquee } from '@/components/generals/marquee'
import { Media } from '@/components/generals/media'
import { Eyebrow } from '@/components/generals/section-heading'
import { SplitWords } from '@/components/generals/split-words'
import { allSkills, educationContent } from '@/constants/content'
import { media } from '@/constants/media'

interface EducationProps {
    educations: EducationRow[]
}

/** Education history beside two photos, closed by a marquee of the tools I use. */
export const Education: FC<EducationProps> = ({ educations = [] }): ReactNode => {
    const scope = useRef<HTMLElement>(null)

    useReveal(scope, [educations.length])

    return (
        <section ref={scope} id='education' aria-labelledby='education-title' className='relative overflow-hidden bg-bone pt-28 text-ink md:pt-40'>
            <div className='gutter grid gap-14 lg:grid-cols-12 lg:gap-10'>
                <div className='relative lg:col-span-6'>
                    <Media
                        src={media.portraitFormal.src}
                        alt={media.portraitFormal.alt}
                        sizes='(min-width: 1024px) 48vw, 92vw'
                        className='aspect-[4/5]'
                        position='50% 30%'
                        reveal
                        parallax={10}
                    />

                    <div className='absolute -bottom-10 right-4 w-[42%] md:right-[-6%] lg:w-[46%]'>
                        <Media
                            src={media.presenting.src}
                            alt={media.presenting.alt}
                            sizes='(min-width: 1024px) 22vw, 40vw'
                            className='aspect-[4/3] border-[6px] border-bone'
                            position='50% 45%'
                            reveal
                        />
                    </div>
                </div>

                <div className='flex flex-col gap-10 lg:col-span-5 lg:col-start-8 lg:pt-10'>
                    <div className='flex items-center justify-between'>
                        <Eyebrow index='06' className='text-stone-deep'>
                            {educationContent.label}
                        </Eyebrow>

                        <span className='eyebrow text-stone-deep'>
                            {educationContent.since}
                        </span>
                    </div>

                    <h2 id='education-title' className='font-wide text-big font-semibold uppercase tracking-[-0.05em]' data-reveal='words'>
                        <SplitWords text={educationContent.title} />
                        <br />
                        <SplitWords text={educationContent.accent} wordClassName='font-serif font-normal normal-case italic tracking-[-0.02em]' />
                    </h2>

                    <p className='text-lead font-medium' data-reveal='fade'>
                        {educationContent.statement}
                    </p>

                    <p className='max-w-md text-base leading-relaxed text-ink/70' data-reveal='fade'>
                        {educationContent.description}
                    </p>

                    {educations.length > 0 && (
                        <ul className='flex flex-col border-t border-ink/15' data-reveal='stagger'>
                            {educations.map((education, index) => (
                                <li key={education.id} className='flex items-start gap-4 border-b border-ink/15 py-5'>
                                    <span className='flex size-12 shrink-0 items-center justify-center rounded-full border border-ink/20 font-wide text-sm font-semibold'>
                                        {pad(index + 1)}
                                    </span>

                                    <span className='flex flex-1 flex-col gap-1'>
                                        <span className='text-base font-semibold md:text-lg' data-mdlfy-cms={cms('educations', education.id, 'title')}>
                                            {education.title}
                                        </span>
                                        <span className='flex flex-wrap gap-x-2 text-sm text-ink/70'>
                                            {education.field && (
                                                <span data-mdlfy-cms={cms('educations', education.id, 'field')}>{education.field}</span>
                                            )}
                                            {education.field && education.institution && (
                                                <span aria-hidden='true'>/</span>
                                            )}
                                            {education.institution && (
                                                <span data-mdlfy-cms={cms('educations', education.id, 'institution')}>{education.institution}</span>
                                            )}
                                        </span>
                                    </span>

                                    {education.period && (
                                        <span className='eyebrow shrink-0 pt-1 text-right text-stone-deep' data-mdlfy-cms={cms('educations', education.id, 'period')}>
                                            {education.period}
                                        </span>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}

                    <ArrowLink href='/about' className='self-start text-base'>
                        {educationContent.action}
                    </ArrowLink>
                </div>
            </div>

            <div className='gutter mt-32 grid gap-10 border-t border-ink/15 pt-10 md:mt-44 md:grid-cols-12'>
                <div className='flex flex-col gap-4 md:col-span-4'>
                    <Eyebrow className='text-stone-deep'>
                        {educationContent.toolsLabel}
                    </Eyebrow>

                    <h3 className='font-wide text-title font-semibold uppercase tracking-[-0.04em]' data-reveal='words'>
                        <SplitWords text={educationContent.toolsTitle} />{' '}
                        <SplitWords text={educationContent.toolsAccent} wordClassName='font-serif font-normal normal-case italic tracking-[-0.01em]' />
                    </h3>
                </div>

                <div className='flex flex-col gap-4 md:col-span-6 md:col-start-7' data-reveal='fade'>
                    <p className='text-lead text-ink/85'>
                        {educationContent.toolsStatement}
                    </p>

                    <p className='text-sm leading-relaxed text-ink/65'>
                        {educationContent.toolsDescription}
                    </p>
                </div>
            </div>

            <Marquee duration={30} className='mt-14 bg-ink py-10 md:mt-20 md:py-14'>
                {allSkills.map(skill => (
                    <span key={skill} className='mx-8 flex items-center gap-8 text-bone md:mx-12 md:gap-12'>
                        <span className='font-wide text-2xl font-semibold uppercase tracking-[-0.03em] md:text-4xl'>
                            {skill}
                        </span>
                        <span className='size-2 rounded-full bg-flare' aria-hidden='true' />
                    </span>
                ))}
            </Marquee>
        </section>
    )
}
